import React, { useRef, useEffect, memo } from 'react';
import { GridDot, GridCurve } from '../types/AmslerGridData';

interface AmslerGridCardCanvasProps {
  dots: GridDot[] | null | undefined;
  curves: GridCurve[] | null | undefined;
  width: number;
  height: number;
}

const AmslerGridCardCanvas: React.FC<AmslerGridCardCanvasProps> = ({ dots, curves, width, height }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Set canvas dimensions
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    // Fill with white background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    
    // Calculate grid properties
    const gridSize = 19; // 19 squares (20 lines) to match Android implementation
    
    // Calculate cellSize based on the smaller dimension to ensure the grid fits within the canvas
    const cellSize = Math.min(width, height) / gridSize;
    
    // Calculate grid boundaries
    const gridStartX = (width - (cellSize * gridSize)) / 2;
    const gridStartY = (height - (cellSize * gridSize)) / 2;
    const gridEndX = gridStartX + (cellSize * gridSize);
    const gridEndY = gridStartY + (cellSize * gridSize);

    // Draw curves if they exist
    if (curves && curves.length > 0) {
      curves.forEach(curve => {
        if (curve.points && curve.points.length > 0) {
          ctx.beginPath();
          ctx.strokeStyle = curve.color || '#000000';
          ctx.lineWidth = 2;
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';

          // Convert normalized coordinates to canvas coordinates
          const firstPoint = curve.points[0];
          const startX = gridStartX + (firstPoint.x * cellSize * gridSize);
          const startY = gridStartY + (firstPoint.y * cellSize * gridSize);
          ctx.moveTo(startX, startY);

          // Draw the curve
          for (let i = 1; i < curve.points.length; i++) {
            const point = curve.points[i];
            const x = gridStartX + (point.x * cellSize * gridSize);
            const y = gridStartY + (point.y * cellSize * gridSize);
            ctx.lineTo(x, y);
          }

          ctx.stroke();
        }
      });
    }
    
    // Process dots, ensuring they're contained within the grid boundaries
    const normalizedDots: {x: number, y: number}[] = [];
    if (dots && Array.isArray(dots) && dots.length > 0) {
      dots.forEach(dot => {
        if (typeof dot.x === 'number' && typeof dot.y === 'number') {
          // Convert normalized coordinates (0-1) to canvas/grid coordinates
          // and ensure they're within grid boundaries
          const x = gridStartX + (Math.min(Math.max(dot.x, 0), 1) * cellSize * gridSize);
          const y = gridStartY + (Math.min(Math.max(dot.y, 0), 1) * cellSize * gridSize);
          
          // Only add dots that are within the grid boundaries
          if (x >= gridStartX && x <= gridEndX && y >= gridStartY && y <= gridEndY) {
            normalizedDots.push({ x, y });
          }
        }
      });
    }
    
    // Process distortion areas if dots exist
    const distortionAreas: {x: number, y: number, radius: number}[] = [];
    normalizedDots.forEach(dot => {
      distortionAreas.push({
        x: dot.x,
        y: dot.y,
        radius: cellSize * 2.5 // Slightly reduced radius to keep distortions within grid
      });
    });
    
    // Create distortion grid if needed
    let distortedGrid: {x: number, y: number}[][] | null = null;
    
    if (distortionAreas.length > 0) {
      // Create grid points matrix for distortion
      distortedGrid = Array(gridSize + 1).fill(0).map(() => 
        Array(gridSize + 1).fill(0).map(() => ({ x: 0, y: 0 }))
      );
      
      // Calculate distorted positions
      for (let i = 0; i <= gridSize; i++) {
        for (let j = 0; j <= gridSize; j++) {
          const origX = gridStartX + (j * cellSize);
          const origY = gridStartY + (i * cellSize);
          
          // Start with original position
          let newX = origX;
          let newY = origY;
          
          // Apply distortion from each anomaly dot
          for (const area of distortionAreas) {
            const dx = origX - area.x;
            const dy = origY - area.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            
            // Apply distortion only within radius
            if (distance < area.radius) {
              // Calculate distortion strength (smoother falloff)
              const factor = Math.pow(1 - distance / area.radius, 2.5) * 0.15; 
              
              // Calculate direction and apply shift
              const angle = Math.atan2(dy, dx);
              newX += Math.cos(angle) * factor * cellSize;
              newY += Math.sin(angle) * factor * cellSize;
            }
          }
          
          // Ensure the distorted point doesn't go outside the grid boundaries
          newX = Math.min(Math.max(newX, gridStartX), gridEndX);
          newY = Math.min(Math.max(newY, gridStartY), gridEndY);
          
          distortedGrid[i][j] = { x: newX, y: newY };
        }
      }
    }
    
    // Draw distortion area highlights
    if (distortionAreas.length > 0) {
      // Create a radial gradient for smoother distortion areas
      for (const area of distortionAreas) {
        const gradient = ctx.createRadialGradient(
          area.x, area.y, 0,
          area.x, area.y, area.radius
        );
        gradient.addColorStop(0, 'rgba(150, 0, 0, 0.15)');
        gradient.addColorStop(0.7, 'rgba(150, 0, 0, 0.07)');
        gradient.addColorStop(1, 'rgba(150, 0, 0, 0)');
        
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(area.x, area.y, area.radius, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // Draw grid lines - darker grid lines on white background
    ctx.strokeStyle = '#666666';
    ctx.lineWidth = 1.0;
    
    // Apply anti-aliasing for smoother lines
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    
    // Draw horizontal lines
    for (let i = 0; i <= gridSize; i++) {
      ctx.beginPath();
      
      if (distortedGrid) {
        // Draw distorted lines with blur in distorted areas
        for (let j = 0; j < gridSize; j++) {
          const start = distortedGrid[i][j];
          const end = distortedGrid[i][j+1];
          
          // Check if this segment is in a distortion area
          const midX = (start.x + end.x) / 2;
          const midY = (start.y + end.y) / 2;
          let inDistortionArea = false;
          
          for (const area of distortionAreas) {
            const dx = midX - area.x;
            const dy = midY - area.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            
            if (distance < area.radius * 0.8) {
              inDistortionArea = true;
              break;
            }
          }
          
          // Apply blur effect and red color for distorted segments
          if (inDistortionArea) {
            ctx.save();
            ctx.shadowColor = 'rgba(150, 0, 0, 0.4)';
            ctx.shadowBlur = 1.5;
            ctx.strokeStyle = 'rgba(150, 0, 0, 0.9)';
            ctx.lineWidth = 0.8;
          } else {
            ctx.strokeStyle = '#666666';
            ctx.lineWidth = 1;
          }
          
          // Draw line segment
          ctx.beginPath();
          ctx.moveTo(start.x, start.y);
          ctx.lineTo(end.x, end.y);
          ctx.stroke();
          
          if (inDistortionArea) {
            ctx.restore();
          }
        }
      } else {
        // Draw straight lines
        const y = gridStartY + (i * cellSize);
        ctx.moveTo(gridStartX, y);
        ctx.lineTo(gridEndX, y);
        ctx.stroke();
      }
    }
    
    // Draw vertical lines
    for (let j = 0; j <= gridSize; j++) {
      if (distortedGrid) {
        // Draw distorted lines with blur in distorted areas
        for (let i = 0; i < gridSize; i++) {
          const start = distortedGrid[i][j];
          const end = distortedGrid[i+1][j];
          
          // Check if this segment is in a distortion area
          const midX = (start.x + end.x) / 2;
          const midY = (start.y + end.y) / 2;
          let inDistortionArea = false;
          
          for (const area of distortionAreas) {
            const dx = midX - area.x;
            const dy = midY - area.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            
            if (distance < area.radius * 0.8) {
              inDistortionArea = true;
              break;
            }
          }
          
          // Apply blur effect and red color for distorted segments
          if (inDistortionArea) {
            ctx.save();
            ctx.shadowColor = 'rgba(150, 0, 0, 0.4)';
            ctx.shadowBlur = 1.5;
            ctx.strokeStyle = 'rgba(150, 0, 0, 0.9)';
            ctx.lineWidth = 0.8;
          } else {
            ctx.strokeStyle = '#666666';
            ctx.lineWidth = 1;
          }
          
          // Draw line segment
          ctx.beginPath();
          ctx.moveTo(start.x, start.y);
          ctx.lineTo(end.x, end.y);
          ctx.stroke();
          
          if (inDistortionArea) {
            ctx.restore();
          }
        }
      } else {
        // Draw straight lines
        const x = gridStartX + (j * cellSize);
        ctx.moveTo(x, gridStartY);
        ctx.lineTo(x, gridEndY);
        ctx.stroke();
      }
    }

    // Draw dots
    if (normalizedDots.length > 0) {
      ctx.fillStyle = 'rgba(150, 0, 0, 0.8)';
      normalizedDots.forEach(dot => {
        ctx.beginPath();
        ctx.arc(dot.x, dot.y, 3, 0, Math.PI * 2);
        ctx.fill();
      });
    }
    
    // Draw center dot (small black dot in the center of the grid)
    const centerX = width / 2;
    const centerY = height / 2;
    ctx.fillStyle = '#000000';
    ctx.beginPath();
    ctx.arc(centerX, centerY, 1.5, 0, Math.PI * 2);
    ctx.fill();
  }, [dots, curves, width, height]);

  return <canvas ref={canvasRef} style={{ width: '100%', height: '100%', maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />;
};

// Memoize to prevent unnecessary re-renders
export default memo(AmslerGridCardCanvas); 